let viewportListenersBound = false;
let designModeListenersBound = false;
let storyObserver = null;

const getSliderEl = () => document.querySelector(".ysl-swiper");
const getActiveSwiper = () => getSliderEl()?.swiper;

const storyTimers = new WeakMap();

const clearStoryTimers = (storyEl) => {
  const timers = storyTimers.get(storyEl);
  if (!timers) return;
  timers.forEach((timerId) => clearTimeout(timerId));
  storyTimers.delete(storyEl);
};

const resetStoryItem = (item) => {
  item.classList.remove("is-visible");

  item.querySelectorAll("[data-story-content]").forEach((inner) => {
    inner.classList.remove("is-swapping-out", "is-visible");
    const isPrimary = inner.dataset.storyContent === "primary";
    inner.toggleAttribute("hidden", !isPrimary);
  });
};

const getStoryOverlayCta = (storyEl) =>
  storyEl.closest(".ysl-slide")?.querySelector(".ysl-slide__overlay-cta--delayed");

const resetStoryOverlayCta = (storyEl) => {
  getStoryOverlayCta(storyEl)?.classList.remove("is-visible");
};

const showStoryOverlayCta = (storyEl) => {
  const cta = getStoryOverlayCta(storyEl);
  if (!cta) return;
  cta.classList.add("is-visible");
};

const resetStory = (storyEl) => {
  clearStoryTimers(storyEl);
  storyEl.classList.remove("is-active", "is-playing");
  delete storyEl.dataset.storyRun;
  resetStoryOverlayCta(storyEl);
  storyEl.querySelectorAll("[data-story-item]").forEach(resetStoryItem);
};

const showStoryItem = (storyEl, index) => {
  const item = storyEl.querySelector(`[data-story-item="${index}"]`);
  if (!item) return;
  item.classList.add("is-visible");
};

const swapStoryItemToAlt = (storyEl, runId, index) => {
  if (storyEl.dataset.storyRun !== runId) return;

  const item = storyEl.querySelector(`[data-story-item="${index}"]`);
  if (!item) return;

  const primary = item.querySelector('[data-story-content="primary"]');
  const alt = item.querySelector('[data-story-content="alt"]');
  if (!alt) return;

  const revealAlt = () => {
    if (storyEl.dataset.storyRun !== runId) return;
    if (primary) {
      primary.setAttribute("hidden", "");
      primary.classList.remove("is-swapping-out");
    }
    alt.removeAttribute("hidden");
    alt.classList.add("is-visible");
  };

  if (primary && !primary.hasAttribute("hidden")) {
    primary.classList.add("is-swapping-out");
    const swapTimer = window.setTimeout(revealAlt, 550);
    const timers = storyTimers.get(storyEl) || [];
    timers.push(swapTimer);
    storyTimers.set(storyEl, timers);
    return;
  }

  revealAlt();
};

const playStory = (storyEl) => {
  if (!storyEl || storyEl.classList.contains("is-playing")) return;

  const hasContent = storyEl.querySelector('[data-story-item] [data-story-content="primary"]');
  if (!hasContent) {
    // Story markup present but empty — still reveal the third-line CTA
    showStoryOverlayCta(storyEl);
    return;
  }

  const runId = String(Date.now());
  resetStory(storyEl);
  storyEl.dataset.storyRun = runId;
  storyEl.classList.add("is-active", "is-playing");

  const isDesignMode =
    typeof window.Shopify !== "undefined" && Boolean(window.Shopify.designMode);
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reducedMotion || isDesignMode) {
    showStoryItem(storyEl, 1);
    showStoryItem(storyEl, 2);
    showStoryItem(storyEl, 3);
    showStoryOverlayCta(storyEl);
    swapStoryItemToAlt(storyEl, runId, 1);
    swapStoryItemToAlt(storyEl, runId, 2);
    return;
  }

  const firstDelay = Number(storyEl.dataset.storyFirstDelay || 1000);
  const stepDelay = Number(storyEl.dataset.storyStepDelay || 5000);
  const firstAt = Math.max(0, firstDelay - 500);
  const timers = [];

  const schedule = (callback, delay) => {
    timers.push(
      window.setTimeout(() => {
        if (storyEl.dataset.storyRun !== runId) return;
        callback();
      }, delay)
    );
  };

  schedule(() => showStoryItem(storyEl, 1), firstAt);
  schedule(() => showStoryItem(storyEl, 2), firstAt + stepDelay);
  schedule(() => showStoryItem(storyEl, 3), firstAt + stepDelay * 2);
  schedule(() => showStoryOverlayCta(storyEl), firstAt + stepDelay * 3);
  schedule(() => {
    swapStoryItemToAlt(storyEl, runId, 1);
    swapStoryItemToAlt(storyEl, runId, 2);
  }, firstAt + stepDelay * 4);

  storyTimers.set(storyEl, timers);
};

const bindStoryObserver = () => {
  if (storyObserver) {
    storyObserver.disconnect();
    storyObserver = null;
  }

  const stories = document.querySelectorAll("[data-ysl-slide-story]");
  if (!stories.length || typeof IntersectionObserver === "undefined") return;

  storyObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const storyEl = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          playStory(storyEl);
          return;
        }
        if (!entry.isIntersecting) {
          resetStory(storyEl);
        }
      });
    },
    {
      root: getSliderEl(),
      threshold: [0, 0.55, 1]
    }
  );

  stories.forEach((storyEl) => storyObserver.observe(storyEl));
};

const initYslHomeSlider = () => {
  const sliderEl = getSliderEl();
  if (!sliderEl || typeof Swiper === "undefined") return;

  if (sliderEl.swiper) {
    sliderEl.swiper.destroy(true, true);
  }

  const sliderRoot = document.querySelector("[data-ysl-home-slider]");
  const header =
    document.querySelector("#header-component") ||
    document.querySelector("[data-header]");
  const headerGroup = document.querySelector("#header-group");
  const sliderSection = document.querySelector(".section-ysl-home-slider");
  const mobileQuery = window.matchMedia("(max-width: 749px)");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const isDesignMode =
    typeof window.Shopify !== "undefined" && Boolean(window.Shopify.designMode);

  document.body.classList.add("has-ysl-home-slider");

  const syncHeader = () => {
    if (!header) return;
    header.classList.add("ysl-header-overlay");
    header.classList.remove("ysl-header-dark");
    header.classList.add("ysl-header-light");
  };

  const measureMobileViewport = () => {
    if (!mobileQuery.matches || !sliderSection) return;

    const visualViewport = window.visualViewport;
    const viewportHeight = Math.round(
      visualViewport?.height ?? window.innerHeight
    );
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const sliderTop = Math.round(sliderSection.getBoundingClientRect().top);
    const headerOffset = Math.max(0, sliderTop - viewportTop);
    const sliderHeight = Math.max(0, viewportHeight - headerOffset);

    const root = document.documentElement;
    root.style.setProperty("--ysl-viewport-height", `${viewportHeight}px`);
    root.style.setProperty("--ysl-header-offset", `${headerOffset}px`);
    root.style.setProperty("--ysl-slider-height", `${sliderHeight}px`);
  };

  let isAnimating = false;
  let isTouching = false;

  const getSwiper = () => sliderEl.swiper;

  const canSyncLayout = () => !isTouching && !isAnimating;

  const syncLayout = ({ force = false } = {}) => {
    if (!force && !canSyncLayout()) return;
    measureMobileViewport();
    if (!force && !canSyncLayout()) return;
    getSwiper()?.update();
  };

  const isFirstSlideAutoplayEnabled = () => {
    if (isDesignMode) return false;
    return sliderRoot?.dataset.firstSlideAutoplay !== "false";
  };

  const autoplayDelayMs = () =>
    Math.max(0, Number(sliderRoot?.dataset.firstSlideAutoplayDelay || 3)) * 1000;

  const shouldUseFirstSlideAutoplay = () =>
    isFirstSlideAutoplayEnabled() && !reducedMotionQuery.matches;

  const stopFirstSlideAutoplay = (swiper) => {
    swiper?.autoplay?.stop?.();
    swiper?.mousewheel?.enable?.();
  };

  const bindViewportListeners = () => {
    if (viewportListenersBound) return;
    viewportListenersBound = true;

    window.addEventListener("resize", () => syncLayout(), { passive: true });
    window.addEventListener("orientationchange", () => syncLayout({ force: true }), {
      passive: true
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", () => syncLayout(), {
        passive: true
      });
    }

    if (headerGroup && typeof ResizeObserver !== "undefined") {
      new ResizeObserver(() => syncLayout()).observe(headerGroup);
    }

    mobileQuery.addEventListener("change", () => syncLayout({ force: true }));
  };

  measureMobileViewport();
  bindViewportListeners();

  const useAutoplay = shouldUseFirstSlideAutoplay();

  new Swiper(sliderEl, {
    direction: "vertical",
    slidesPerView: 1,
    slidesPerGroup: 1,
    speed: 650,
    effect: "slide",
    autoHeight: false,
    resistanceRatio: 0.85,
    longSwipes: true,
    longSwipesRatio: 0.5,
    longSwipesMs: 300,
    shortSwipes: true,
    threshold: 12,
    touchAngle: 45,
    followFinger: true,
    watchOverflow: true,
    preventInteractionOnTransition: true,
    touchReleaseOnEdges: false,
    touchStartPreventDefault: false,
    autoplay: useAutoplay
      ? {
          delay: autoplayDelayMs(),
          disableOnInteraction: false,
          waitForTransition: true
        }
      : false,
    mousewheel: {
      enabled: !useAutoplay,
      forceToAxis: true,
      releaseOnEdges: false,
      thresholdDelta: 20,
      thresholdTime: 300,
      sensitivity: 1,
      eventsTarget: sliderEl
    },
    keyboard: {
      enabled: true,
      onlyInViewport: true
    },
    on: {
      init(swiper) {
        syncHeader();
        bindStoryObserver();
        requestAnimationFrame(() => {
          syncLayout({ force: true });
          requestAnimationFrame(() => syncLayout({ force: true }));
        });

        if (useAutoplay) {
          swiper.mousewheel?.disable?.();
          window.setTimeout(() => {
            if (swiper.activeIndex === 0) {
              swiper.autoplay?.start?.();
            }
          }, 100);
          window.setTimeout(() => {
            swiper.mousewheel?.enable?.();
          }, autoplayDelayMs() + 1500);
        }
      },
      slideChange(swiper) {
        syncHeader();
        if (swiper.activeIndex >= 1) {
          stopFirstSlideAutoplay(swiper);
        }
      },
      slideChangeTransitionEnd(swiper) {
        isAnimating = false;
        bindStoryObserver();
        const storyEl = swiper.slides[swiper.activeIndex]?.querySelector("[data-ysl-slide-story]");
        if (storyEl) {
          window.setTimeout(() => playStory(storyEl), 80);
        }
      },
      touchStart() {
        isTouching = true;
      },
      touchEnd() {
        isTouching = false;
      },
      transitionStart() {
        isAnimating = true;
      },
      transitionEnd() {
        isAnimating = false;
      }
    }
  });

  sliderEl.addEventListener(
    "wheel",
    (event) => {
      if (isAnimating || isTouching) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  if (isDesignMode && !designModeListenersBound) {
    designModeListenersBound = true;

    document.addEventListener("shopify:block:select", (event) => {
      const section = event.target?.closest?.(".section-ysl-home-slider");
      if (!section) return;
      const slider = section.querySelector(".ysl-swiper")?.swiper;
      if (!slider) return;
      const slide = event.target.closest(".swiper-slide");
      if (!slide) return;
      const index = Array.from(slider.slides).indexOf(slide);
      if (index < 0) return;
      slider.slideTo(index, 0);
      window.setTimeout(() => {
        const storyEl = slide.querySelector("[data-ysl-slide-story]");
        if (storyEl) playStory(storyEl);
      }, 50);
    });
  }
};

const bootYslHomeSlider = () => {
  initYslHomeSlider();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootYslHomeSlider);
} else {
  bootYslHomeSlider();
}

document.addEventListener("shopify:section:load", (event) => {
  if (event.target?.querySelector?.(".ysl-swiper")) {
    bootYslHomeSlider();
  }
});
